import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useApplyCarePathway } from '../state/data/pathway';
import { useCompleteTask } from '../state/data/task';
import { useUpdatePatientCategory } from '../state/data/patient';
import { useSendChatMessage } from '../state/data/chat';
import SubtrackPicker from './subtrack/SubtrackPicker';
import { hasDiagnosisChange } from '../logic/pathwayConditions';
import CmsEligibilityModal from './CmsEligibilityModal';
import { IntakeBody, normalizeIntake } from './PatientIntakesList';
import { ELATION_PATIENT_BASE_URL } from '../config/medical-constants';
import {
  findOpenSubtrackConfirm, readAccessSubtrack, buildReviewNote, isCmsTestPatient,
} from '../logic/onboardingReview';
import { completionActorOf } from '../logic/task';

// First-time care-team review. On finish: pathway_apply_requested stamps the durable
// context.onboarding_review gate (WF36→WF15). carePlan MUST be the context-bearing Tier-1 plan.
const STEPS = ['Verify', 'Read intakes', 'Confirm plan', 'Get oriented'];
const CONFIRM_PLAN_STEP = STEPS.indexOf('Confirm plan');
const COMPLEMENTARY_INTAKE_TYPE = 'enrollment_complementary_intake_regular';

export default function OnboardingReviewModal({
  patient, carePlan, carePeriod, eligibility, consent, insurance, intakes,
  tasks, currentUser, userRole, onClose, onFinished, onEligibilityChecked,
}) {
  const [step, setStep] = useState(0);
  const [elationVerified, setElationVerified] = useState(false);
  const [consentVerified, setConsentVerified] = useState(false);
  const [eligibilityReviewed, setEligibilityReviewed] = useState(false);
  const [eligibilityOpen, setEligibilityOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const [error, setError] = useState(null);

  const applyMut = useApplyCarePathway();
  const completeTaskMut = useCompleteTask();
  const updateCategoryMut = useUpdatePatientCategory();
  const submitting = applyMut.isPending || completeTaskMut.isPending || updateCategoryMut.isPending;

  const onSelectionChange = useCallback((s) => setSel(s), []);

  // Escape + body scroll lock (modal pattern). `eligibilityOpen` is in deps for two
  // reasons: swallow Escape while the nested modal is open (it owns its own close), and
  // re-lock body scroll after that child's unmount cleanup releases it.
  useEffect(() => {
    // `submitting` also gates Escape: the finish flow now waits (up to 30s) for the
    // pathway apply to be poll-confirmed — abandoning mid-confirmation loses it.
    const onKey = (e) => { if (e.key === 'Escape' && !eligibilityOpen && !submitting) onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, eligibilityOpen, submitting]);

  const firstName = patient.firstname || patient.first_name || '';
  const lastName = patient.lastname || patient.last_name || '';
  // Dedup identical first/last (test data → "X X") into a clean display name.
  const displayName = firstName && lastName && firstName.toLowerCase() === lastName.toLowerCase()
    ? firstName
    : [firstName, lastName].filter(Boolean).join(' ').trim() || 'New patient';
  const age = useMemo(() => computeAge(patient, insurance), [patient, insurance]);
  const topPriority = useMemo(() => firstTopPriority(intakes), [intakes]);
  const suggested = useMemo(() => readAccessSubtrack(carePlan), [carePlan]);
  const gateTask = useMemo(() => findOpenSubtrackConfirm(tasks), [tasks]);
  const reviewerName = currentUser
    ? [currentUser.firstname || currentUser.shortname, currentUser.lastname].filter(Boolean).join(' ') || currentUser.accountid || null
    : null;

  async function handleFinish() {
    if (!sel?.selectedId) { setStep(CONFIRM_PLAN_STEP); setError('Select and confirm a subtrack to finish.'); return; }
    setError(null);
    try {
      // 1. Apply the subtrack + mark the plan reviewed. WF36 stamps the durable
      //    context.onboarding_review gate AND (via approvedBy) sets approved_by/at
      //    so the care plan reads "reviewed" — the first care-team review IS the sign-off.
      const outcome = await applyMut.mutateAsync({
        withingsUserId: patient.userid,
        selected: [sel.selectedId],
        ckdStage: sel.ckdStage,
        approvedBy: reviewerName,
        // The diagnosis shown in the picker's preview — Finish is the attestation.
        confirmedConditions: sel.diagnosisDelta?.proposed ?? null,
      });
      // confirmed:false = the event was accepted but the plan write wasn't confirmed
      // within the poll window — finish the review anyway (the apply may still land);
      // the shell's onFinished toast says so.
      // 2. Set the program category so Withings records this patient's device measures (BP chart source). Best-effort — never block a completed review on a program-side write.
      try {
        await updateCategoryMut.mutateAsync({ userid: patient.userid, category: 0 });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(`updatePatientCategory failed for ${patient.userid}:`, e?.message);
      }
      // 3. Secondary — close the clinician dashboard task if one is open.
      if (gateTask?.task_id) {
        const note = buildReviewNote({
          reviewerName, reviewerRole: userRole,
          elationVerified, consentVerified,
          subtrackId: sel.selectedId, resolvedSubtrackId: suggested?.subtrack_id ?? null,
          at: new Date().toISOString(),
        });
        try {
          await completeTaskMut.mutateAsync({
            taskId: gateTask.task_id, withingsUserId: patient.userid, notes: note,
            actor: completionActorOf(currentUser, userRole),
          });
        } catch { /* secondary signal — never block the finish on the task PATCH */ }
      }
      onFinished?.(outcome);
    } catch (e) {
      setError(e?.message || 'Could not confirm — please retry.');
    }
  }

  // Finish needs a selection + either a landed preview OR a surfaced resolve error
  // (don't strand the user when the advisory preview fails — apply re-resolves server-side).
  const canFinish = !submitting && !!sel?.selectedId && (!!sel?.preview || !!sel?.resolveError);

  // Step-1 gate: the care team must clear all three checks before advancing.
  // Eligibility auto-satisfies (locked green) when the patient is already known
  // eligible — either the eligibility CHECK task completed, or the care period has
  // moved past eligibility (pending_alignment / active). Otherwise it's a tickable
  // ack, and a "Check eligibility" button runs the CMS check in place.
  const eligibleResolved = eligibility?.taskStatus === 'completed'
    || carePeriod?.status === 'pending_alignment'
    || carePeriod?.status === 'active';
  const step1Complete = elationVerified && consentVerified && (eligibleResolved || eligibilityReviewed);

  return createPortal((
    <>
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        // Below the standard .modal-overlay (z-index 1000) so the nested CMS
        // eligibility modal layers explicitly above this review, not by append order.
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 990,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--wi-background-secondary)', borderRadius: 14,
          width: 880, maxWidth: '94vw', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.22)', fontFamily: 'var(--wi-font-family)',
        }}
      >
        {/* ── HEADER ── */}
        <div style={{
          padding: '18px 24px 16px', borderBottom: '1px solid var(--wi-divider)',
          display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0,
        }}>
          <Avatar name={displayName} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, letterSpacing: '-0.18px', color: 'var(--wi-text-primary)' }}>
              New patient — first care-team review
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 3 }}>
              <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--wi-text-secondary)' }}>
                {displayName}
              </span>
              {age != null && (
                <span style={{
                  fontSize: 11.5, fontWeight: 600, padding: '2px 9px', borderRadius: 999,
                  background: 'var(--wi-background-primary)', color: 'var(--wi-text-secondary)',
                }}>{age} yrs</span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 32, height: 32, borderRadius: '50%', background: 'var(--wi-background-primary)',
              color: 'var(--wi-text-secondary)', border: 'none', cursor: 'pointer', fontSize: 16,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              fontFamily: 'var(--wi-font-family)', alignSelf: 'flex-start',
            }}
          >×</button>
        </div>

        {/* ── BODY ── */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px', background: 'var(--wi-background-primary)' }}>
          {step === 0 && (
            <StepWelcome
              displayName={displayName} firstName={firstName} topPriority={topPriority}
              carePeriod={carePeriod} eligibility={eligibility} consent={consent}
              ehrPatientId={patient.ehr_patient_id}
              isCmsTest={isCmsTestPatient(insurance)}
              elationVerified={elationVerified} setElationVerified={setElationVerified}
              consentVerified={consentVerified} setConsentVerified={setConsentVerified}
              eligibilityReviewed={eligibilityReviewed} setEligibilityReviewed={setEligibilityReviewed}
              eligibleResolved={eligibleResolved}
              onRunEligibility={() => setEligibilityOpen(true)}
            />
          )}
          {step === 1 && (
            <StepIntakes
              intakes={intakes} firstName={firstName} displayName={displayName}
              patient={patient} currentUser={currentUser}
            />
          )}
          {step === 2 && (
            <SubtrackPicker key={patient.userid} patient={patient} carePlan={carePlan} onSelectionChange={onSelectionChange} />
          )}
          {step === 3 && (
            <StepNext tasks={tasks} />
          )}
        </div>

        {/* ── FOOTER ── */}
        <div style={{
          borderTop: '1px solid var(--wi-divider)', padding: '14px 24px',
          background: 'var(--wi-background-secondary)', flexShrink: 0,
        }}>
          {error && (
            <div style={{
              background: 'var(--wi-background-bad)', color: 'var(--wi-status-bad)',
              padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 13,
            }}>{error}</div>
          )}
          {submitting && (
            <div style={{ color: 'var(--wi-text-secondary)', marginBottom: 10, fontSize: 13 }}>
              Waiting for the plan change to be confirmed — this can take up to 30 seconds.
            </div>
          )}
          <div style={{ display: 'flex', gap: 16, justifyContent: 'space-between', alignItems: 'center' }}>
            <ProgressTrack step={step} />
            <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
              {step > 0 && (
                <button className="btn-ok" disabled={submitting} onClick={() => setStep(s => s - 1)}
                  style={{ cursor: submitting ? 'not-allowed' : 'pointer' }}>Back</button>
              )}
              {step < STEPS.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={() => { setError(null); setStep(s => s + 1); }}
                  disabled={step === 0 && !step1Complete}
                  title={step === 0 && !step1Complete ? 'Verify all three items to continue' : undefined}
                  style={{ cursor: (step === 0 && !step1Complete) ? 'not-allowed' : 'pointer', opacity: (step === 0 && !step1Complete) ? 0.55 : 1 }}
                >
                  Next
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleFinish}
                  disabled={!canFinish}
                  style={{ cursor: canFinish ? 'pointer' : 'not-allowed', opacity: canFinish ? 1 : 0.55 }}
                >
                  {submitting ? 'Confirming…' : hasDiagnosisChange(sel?.diagnosisDelta ?? null) ? 'Confirm & update diagnosis' : 'Confirm & finish'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Nested CMS eligibility check. onSaved fires on terminal verdict → refreshBundle re-derives the card. */}
    {eligibilityOpen && (
      <CmsEligibilityModal
        patient={patient}
        carePeriodId={carePeriod?.care_period_id}
        taskId={null}
        onClose={() => setEligibilityOpen(false)}
        onSaved={() => { setEligibilityOpen(false); onEligibilityChecked?.(); }}
      />
    )}
    </>
  ), document.body);
}

/* ─────────────── Step 1 — Welcome & verify ─────────────── */
function StepWelcome({
  displayName, firstName, topPriority, carePeriod, eligibility, consent,
  ehrPatientId, isCmsTest, elationVerified, setElationVerified, consentVerified, setConsentVerified,
  eligibilityReviewed, setEligibilityReviewed, eligibleResolved, onRunEligibility,
}) {
  // Eligibility verdict: ineligible if CMS said so; eligible if the check passed
  // OR the care period already moved past eligibility (pending_alignment / active);
  // otherwise not yet checked → offer to run it here.
  const eligState = carePeriod?.status === 'ineligible'
    ? 'ineligible'
    : (eligibleResolved ? 'eligible' : 'not_checked');
  const eligSatisfied = eligibleResolved || eligibilityReviewed;
  const eligBody = {
    eligible: eligibility?.summary ? String(eligibility.summary) : 'Eligible for the ACCESS program — CMS eligibility confirmed.',
    ineligible: eligibility?.summary ? String(eligibility.summary) : 'CMS returned ineligible — review before proceeding.',
    not_checked: 'CMS eligibility hasn’t been checked yet. Run the check to confirm this patient qualifies.',
  }[eligState];
  const verifiedCount = (eligSatisfied ? 1 : 0) + (elationVerified ? 1 : 0) + (consentVerified ? 1 : 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{
        background: 'var(--wi-background-info)', color: 'var(--wi-text-primary-info)',
        borderRadius: 12, padding: '16px 18px',
      }}>
        <div style={{ fontSize: 16, fontWeight: 600 }}>
          {firstName || displayName} just joined the program <span aria-hidden="true">🎉</span>
        </div>
        <div style={{ fontSize: 13.5, marginTop: 4, lineHeight: 1.5 }}>
          First time the care team is opening this file — a couple of quick checks before you get started.
        </div>
        {topPriority && (
          <div style={{ fontSize: 13, marginTop: 8, opacity: 0.9 }}>
            <span style={{ fontWeight: 600 }}>Their top priority:</span> “{topPriority}”
          </div>
        )}
      </div>

      {/* Verification checklist — all three must clear to advance */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div className="eyebrow">Verify before continuing</div>
          <span style={{
            fontSize: 11.5, fontWeight: 600, padding: '2px 10px', borderRadius: 999,
            background: verifiedCount === 3 ? 'var(--wi-background-good)' : 'var(--wi-background-primary)',
            color: verifiedCount === 3 ? 'var(--wi-text-primary-good)' : 'var(--wi-text-secondary)',
            transition: 'background 150ms ease, color 150ms ease',
          }}>{verifiedCount} of 3 verified</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <CheckCard
            checked={eligSatisfied} onChange={setEligibilityReviewed} disabled={eligibleResolved}
            title="CMS eligibility"
            body={eligBody}
            action={eligState === 'not_checked' && onRunEligibility ? (
              <button
                type="button"
                className="btn-primary"
                style={{ fontSize: 12.5, padding: '6px 14px' }}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRunEligibility(); }}
              >Check eligibility</button>
            ) : null}
            trailing={<EligibilityChip state={eligState} />}
          />
          <CheckCard
            checked={elationVerified} onChange={setElationVerified}
            // A CMS synthetic test patient has no chart by design — don't block the
            // review on a record that was never meant to exist.
            disabled={!ehrPatientId && !isCmsTest}
            title="Elation record"
            body={!ehrPatientId && isCmsTest
              ? 'TEST PATIENT, NO FILE CREATED — CMS synthetic beneficiary, no Elation chart expected.'
              : 'Patient record was created and looks correct.'}
            action={ehrPatientId ? (
              <a
                href={`${ELATION_PATIENT_BASE_URL}/${ehrPatientId}/`}
                target="_blank" rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ color: 'var(--wi-status-info)', textDecoration: 'underline', whiteSpace: 'nowrap', fontSize: 12.5 }}
              >Open in Elation ↗</a>
            ) : (
              <span style={{ color: isCmsTest ? 'var(--wi-text-secondary)' : 'var(--wi-status-bad)', fontSize: 12.5 }}>
                {isCmsTest ? 'TEST PATIENT, NO FILE CREATED' : 'No Elation ID — onboarding may have failed.'}
              </span>
            )}
          />
          <CheckCard
            checked={consentVerified} onChange={setConsentVerified}
            title="Consent note"
            body={`Consent note is created and signed in Elation${
              consent?.signer_typed_name
                ? ` (signed: ${consent.signer_typed_name}${consent.signed_at ? `, ${fmtDate(consent.signed_at)}` : ''})`
                : ''}.`}
            action={ehrPatientId && (
              <a
                href={`${ELATION_PATIENT_BASE_URL}/${ehrPatientId}/`}
                target="_blank" rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ color: 'var(--wi-status-info)', textDecoration: 'underline', whiteSpace: 'nowrap', fontSize: 12.5 }}
              >View chart ↗</a>
            )}
          />
        </div>
      </div>
    </div>
  );
}

/* ─────────────── Step 2 — Read the intakes ─────────────── */
// Onboarding funnel intake + long-form complementary health history, read before
// the pathway is confirmed. Missing complementary → notice + one-tap care-team nudge.
function StepIntakes({ intakes, firstName, displayName, patient, currentUser }) {
  const rows = useMemo(
    () => (Array.isArray(intakes) ? intakes : []).map(normalizeIntake).filter(Boolean),
    [intakes],
  );
  // Newest of each type (rows come newest-first from the bundle).
  const onboarding = rows.find(r => r.intake_type === 'onboarding') || null;
  const complementary = rows.find(r => r.intake_type === COMPLEMENTARY_INTAKE_TYPE) || null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ fontSize: 13.5, color: 'var(--wi-text-secondary)', lineHeight: 1.5 }}>
        Read what {firstName || displayName} told us before you confirm their care pathway.
      </div>

      <IntakeSection
        title="Onboarding intake"
        subtitle="From the enrollment funnel"
        intake={onboarding}
        emptyText="No onboarding intake on file for this patient."
      />

      {complementary ? (
        <IntakeSection
          title="Complementary intake"
          subtitle="Long-form health history"
          intake={complementary}
        />
      ) : (
        <ComplementaryMissing
          firstName={firstName} displayName={displayName}
          patient={patient} currentUser={currentUser}
        />
      )}
    </div>
  );
}

function IntakeSection({ title, subtitle, intake, emptyText }) {
  return (
    <div style={{
      background: 'var(--wi-background-secondary)', border: '1px solid var(--wi-divider)',
      borderRadius: 12, overflow: 'hidden',
    }}>
      <div style={{
        padding: '12px 16px', borderBottom: intake ? '1px solid var(--wi-divider)' : 'none',
        display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--wi-text-primary)' }}>{title}</span>
        {subtitle && <span style={{ fontSize: 11.5, color: 'var(--wi-text-secondary)' }}>{subtitle}</span>}
        {intake?.submitted_at && (
          <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--wi-text-placeholder)', whiteSpace: 'nowrap' }}>
            {fmtDate(intake.submitted_at)}
          </span>
        )}
      </div>
      <div style={{ padding: intake ? '4px 16px 16px' : '12px 16px' }}>
        {intake
          ? <IntakeBody data={intake.data} intakeType={intake.intake_type} />
          : <span style={{ fontSize: 12.5, color: 'var(--wi-text-secondary)' }}>{emptyText}</span>}
      </div>
    </div>
  );
}

// Complementary intake not submitted yet → say so + a one-tap "Remind patient"
// (chat message + SMS via WF26). No automatic time-based reminder (care-team-driven).
function ComplementaryMissing({ firstName, displayName, patient, currentUser }) {
  const sendChatMut = useSendChatMessage();
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  async function remind() {
    setError(null);
    const who = firstName || displayName || 'there';
    const message = `Hi ${who}, when you have ~10 minutes, please finish your health history form so your care team can finalize your care plan. Thank you!`;
    try {
      await sendChatMut.mutateAsync({
        withingsUserId: patient.userid,
        message,
        options: {
          send_sms: true,
          sent_by: currentUser?.accountid ? String(currentUser.accountid) : undefined,
          sent_by_firstname: (currentUser?.firstname || currentUser?.shortname) || undefined,
          sent_by_lastname: currentUser?.lastname || undefined,
        },
      });
      setSent(true);
    } catch (e) {
      setError(e?.message || 'Could not send the reminder — please retry.');
    }
  }

  return (
    <div style={{
      background: 'var(--wi-background-moderate)', borderRadius: 12, padding: '14px 16px',
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <span aria-hidden="true">📝</span>
        <div style={{ fontSize: 13, color: 'var(--wi-text-primary-moderate)', lineHeight: 1.5 }}>
          <span style={{ fontWeight: 600 }}>Complementary intake not completed yet.</span>{' '}
          The long-form health history is still pending — you can confirm the pathway now and revisit
          once it lands, or nudge {firstName || displayName} to fill it in.
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button
          className="btn-primary"
          onClick={remind}
          disabled={sendChatMut.isPending || sent}
          style={{ cursor: (sendChatMut.isPending || sent) ? 'default' : 'pointer', opacity: (sendChatMut.isPending || sent) ? 0.6 : 1 }}
        >
          {sent ? 'Reminder sent ✓' : sendChatMut.isPending ? 'Sending…' : 'Remind patient'}
        </button>
        <span style={{ fontSize: 11.5, color: 'var(--wi-text-secondary)' }}>
          Sends a chat message + SMS asking them to complete it.
        </span>
      </div>
      {error && (
        <div style={{ fontSize: 12, color: 'var(--wi-status-bad)' }}>{error}</div>
      )}
    </div>
  );
}

/* ─────────────── Step 4 — What's next ─────────────── */
function StepNext({ tasks }) {
  const open = (Array.isArray(tasks) ? tasks : []).filter(isOpenTask);
  const staff = open.filter(t => t.assigned_to_type === 'staff' || t.assigned_to_type === 'system');
  const patientVisible = open.filter(t => t.assigned_to_type === 'patient');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{
        background: 'var(--wi-background-good)', color: 'var(--wi-text-primary-good)',
        borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <span style={{ fontSize: 22, lineHeight: 1 }} aria-hidden="true">🎯</span>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Here’s what’s on their plate</div>
          <div style={{ fontSize: 13, marginTop: 2, opacity: 0.9 }}>
            {open.length === 0
              ? 'No open tasks yet — the queue fills in as cadence and onboarding steps fire.'
              : `${open.length} open task${open.length > 1 ? 's' : ''}. The full list lives in the Tasks tab.`}
          </div>
        </div>
      </div>

      {staff.length > 0 && <TaskGroup label={`Care-team tasks (${staff.length})`} tasks={staff} />}
      {patientVisible.length > 0 && <TaskGroup label={`Patient tasks (${patientVisible.length})`} tasks={patientVisible} />}
    </div>
  );
}

function TaskGroup({ label, tasks }) {
  const shown = tasks.slice(0, 6);
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--wi-text-secondary)', marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {shown.map((t, i) => (
          <div key={t.task_id || i} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
            background: 'var(--wi-background-secondary)', border: '1px solid var(--wi-divider)', borderRadius: 8,
          }}>
            <PriorityDot priority={t.priority} />
            <span style={{ flex: 1, fontSize: 13, color: 'var(--wi-text-primary)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {t.title || t.template?.name || t.template_code || 'Task'}
            </span>
            {t.due_at && <span style={{ fontSize: 11.5, color: 'var(--wi-text-placeholder)', whiteSpace: 'nowrap' }}>{fmtDate(t.due_at)}</span>}
          </div>
        ))}
        {tasks.length > shown.length && (
          <div style={{ fontSize: 12, color: 'var(--wi-text-placeholder)', paddingLeft: 2 }}>
            +{tasks.length - shown.length} more in the Tasks tab
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────── small shared bits ─────────────── */
function Avatar({ name }) {
  const initials = String(name || '')
    .split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
  return (
    <div
      aria-hidden="true"
      style={{
        width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
        background: 'var(--wi-action-primary)', color: 'var(--wi-on-action-primary)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 16, fontWeight: 600, letterSpacing: '0.02em',
      }}
    >{initials}</div>
  );
}

// Numbered stepper: each step carries its ordinal (done → ✓, current → filled,
// upcoming → outlined) so the sequence reads as "step N of M", not as a flat
// to-do list for the current screen. A thin track reinforces overall progress.
function ProgressTrack({ step }) {
  // Fill anchors to the CURRENT step's marker: 0% at step 1, then 1/3 → 2/3 → full.
  // (step+1)/length over-filled — at step 1 of 4 it read as already 25% done.
  const pct = STEPS.length > 1 ? (step / (STEPS.length - 1)) * 100 : 100;
  return (
    <div style={{ flex: 1, minWidth: 0, maxWidth: 460 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 7 }}>
        {STEPS.map((label, i) => {
          const done = i < step;
          const current = i === step;
          const activeColor = done || current ? 'var(--wi-action-primary)' : 'var(--wi-text-placeholder)';
          return (
            <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <span style={{
                flexShrink: 0,
                width: 18, height: 18, borderRadius: '50%',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 700, lineHeight: 1,
                background: done || current ? 'var(--wi-action-primary)' : 'transparent',
                color: done || current ? 'var(--wi-on-action-primary)' : 'var(--wi-text-placeholder)',
                border: done || current ? 'none' : '1px solid var(--wi-divider)',
                transition: 'all 150ms ease',
              }}>{done ? '✓' : i + 1}</span>
              <span style={{
                fontSize: 11, fontWeight: current ? 700 : 500,
                color: current ? 'var(--wi-text-primary)' : activeColor,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                transition: 'color 150ms ease',
              }}>{label}</span>
            </span>
          );
        })}
      </div>
      <div style={{ height: 5, borderRadius: 999, background: 'var(--wi-divider)', overflow: 'hidden' }}>
        <div style={{
          width: `${pct}%`, height: '100%', borderRadius: 999,
          background: 'var(--wi-action-primary)', transition: 'width 200ms ease',
        }} />
      </div>
    </div>
  );
}

// A check that visibly "completes" (green tint + checkmark) when ticked. `disabled`
// dims only when NOT yet checked (e.g. nothing to verify); a checked+disabled card
// stays full-strength green — used for an auto-satisfied/locked item.
function CheckCard({ checked, onChange, disabled, title, body, action, trailing }) {
  return (
    <label style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      cursor: disabled ? 'default' : 'pointer',
      padding: '12px 14px', borderRadius: 10,
      background: checked ? 'var(--wi-background-good)' : 'var(--wi-background-secondary)',
      border: `1px solid ${checked ? 'var(--wi-status-good)' : 'var(--wi-divider)'}`,
      opacity: disabled && !checked ? 0.65 : 1,
      transition: 'background 150ms ease, border-color 150ms ease',
    }}>
      <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0, marginTop: 1 }}>
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          style={{ width: 17, height: 17, accentColor: 'var(--wi-status-good)', cursor: disabled ? 'default' : 'pointer' }}
        />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: checked ? 'var(--wi-text-primary-good)' : 'var(--wi-text-primary)' }}>{title}</span>
          {checked && <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--wi-text-primary-good)' }} aria-hidden="true">✓ verified</span>}
        </div>
        <div style={{ fontSize: 12.5, color: checked ? 'var(--wi-text-primary-good)' : 'var(--wi-text-secondary)', marginTop: 2, lineHeight: 1.45 }}>{body}</div>
        {action && <div style={{ marginTop: 5 }}>{action}</div>}
      </div>
      {trailing && <div style={{ flexShrink: 0, alignSelf: 'center' }}>{trailing}</div>}
    </label>
  );
}

// Eligibility-CHECK state chip (not the care-period lifecycle).
function EligibilityChip({ state }) {
  const MAP = {
    eligible: { label: 'Eligible', bg: 'var(--wi-background-good)', color: 'var(--wi-text-primary-good)' },
    ineligible: { label: 'Ineligible', bg: 'var(--wi-background-bad)', color: 'var(--wi-status-bad)' },
    not_checked: { label: 'Not checked', bg: 'var(--wi-background-moderate)', color: 'var(--wi-text-primary-moderate)' },
  };
  const cfg = MAP[state] || MAP.not_checked;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600,
      padding: '3px 10px', borderRadius: 20, background: cfg.bg, color: cfg.color,
      textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.color }} />
      {cfg.label}
    </span>
  );
}

function PriorityDot({ priority }) {
  const c = priority === 'urgent' || priority === 'high'
    ? 'var(--wi-status-bad)'
    : priority === 'normal' ? 'var(--wi-status-moderate, var(--wi-text-primary-moderate))' : 'var(--wi-divider)';
  return <span title={priority || 'normal'} style={{ width: 8, height: 8, borderRadius: '50%', background: c, flexShrink: 0 }} />;
}

/* ─────────────── helpers ─────────────── */
const CLOSED = new Set(['completed', 'cancelled', 'missed']);
function isOpenTask(t) { return !CLOSED.has(String(t?.status || '')); }

function firstTopPriority(intakes) {
  for (const i of (Array.isArray(intakes) ? intakes : [])) {
    const tp = i?.data?.top_priority;
    if (tp) return String(tp);
  }
  return null;
}

// Withings profile birthdate (epoch seconds) → age; fallback insurance.cardholder_dob (ISO).
function computeAge(patient, insurance) {
  const raw = patient?.birthdate ?? insurance?.cardholder_dob ?? null;
  if (raw == null) return null;
  let d;
  if (typeof raw === 'number') d = new Date(raw * 1000);
  else if (/^\d+$/.test(String(raw))) d = new Date(Number(raw) * 1000);
  else d = new Date(String(raw));
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age >= 0 && age < 130 ? age : null;
}

function fmtDate(v) {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
